/*
 * FOR SALE — fiche d'annonce, direction « Hangar » (fs-listing-detail et ses snippets), sur la page de l'annonce
 * (/pages/listings/<handle>). Ce script ne touche jamais à la zone [data-fs-commerce] (opaque, immobile) ni à la logique
 * d'achat ou de ticket (fs-purchase.js, fs-ticket.js) : il observe [data-fs-detail] et, pour chaque fiche ([data-fs-ld]) :
 *  - groupe les grands nombres selon la langue de la page (fr « 1 000 000 », en « 1,000,000 », tr « 1.000.000 » ;
 *    espaces insécables : jamais coupés), y compris « ~ 48000 » (« env. 48 000 »), « 140.000 » ou « 48000+ » ; les valeurs mises en forme portent .fs-ld-num ; faction et rang
 *    publiés en minuscules (« helios », « basic major ») prennent une capitale ;
 *  - scène : la tuile choisie dans la flotte (ou les flèches ← →) passe en vedette dans la baie (contrat du socle :
 *    src des deux images .fs-bay-hero et .fs-bay-refl, --ship-w, nom, rareté, « 3 / 21 », fondu .is-swapping sauf en
 *    mouvement réduit), les vaisseaux garés suivent ; tuiles en groupe à tabulation itinérante (flèches, Début, Fin) ;
 *    au-delà de 12 tuiles (8 sur téléphone), « Voir les N vaisseaux » déplie le reste ;
 *  - caractéristiques : au-delà de 8 lignes, « Afficher les N autres » ;
 *  - « Copier » la référence et « Partager » l'adresse de la page (presse-papiers ; boutons montrés seulement si la copie
 *    est possible), état annoncé ;
 *  - « Comparer avec une autre annonce <jeu> » : pose la sélection de fs-compare.js (sessionStorage « fs-compare ») et
 *    renvoie à la liste du jeu ; avec une autre annonce déjà choisie, &compare=<autre>,<celle-ci> y ouvre le face-à-face ;
 *  - téléphone : barre d'achat collante ([data-fs-ld-buybar]) quand la zone commerce sort de l'écran
 *    (IntersectionObserver) ; son bouton déclenche l'action visible de la fiche (acheter, ticket), sans la copier ;
 *  - lien « Proposer une annonce » pour le même jeu, montré si le formulaire de proposition existe sur la page.
 * Module ES (lot 8 de l'uniformisation) : éléments créés par el() du catalogue commun (fs-ui.js) ; formatNumber et
 * properName sont exportés pour les tests Node (sans document, rien n'est exécuté).
 */
import { el as h } from './fs-ui.js';
import { t, number } from './fs-i18n.js';

/* Nombres de la langue de la page (fs-i18n.js : dictionnaire posé par fs-i18n-<langue>.js ; sous Node, le français). */
const group = (digits) => number(Number(digits)).replace(/[\u202f\s]/g, ' ');
/* « 1000000 », « ~ 48000 », « ≈48 000 », « 140.000 », « 3.700.000.000 », « 48000+ » ; jamais « 9.400.000.00 » ni un
   code (« EG-88-3 »), ni un nombre de moins de 4 chiffres. */
const NUMBER = /^([~≈]\s?)?(\d{4,15}|\d{1,3}(?:[.,   ]\d{3})+)(\s?\+)?$/;
export const formatNumber = (text) => {
  const match = NUMBER.exec(text.trim());
  if (!match) return null;
  const digits = match[2].replace(/\D/g, '');
  if (digits.length < 4 || digits.length > 15) return null;
  const grouped = `${group(digits)}${match[3] ? ' +' : ''}`;
  return match[1] ? t('listing.detail.about', { value: grouped }) : grouped;
};
/* Nom propre publié en minuscules (« helios », « basic major ») : capitale à chaque mot. */
export const properName = (text) => (/^[a-zà-ÿ][a-zà-ÿ' -]*$/.test(text.trim()) ? text.trim().replace(/(^|[\s-])([a-zà-ÿ])/g, (_, before, letter) => before + letter.toUpperCase()) : null);

(() => {
  if (typeof document === 'undefined' || window.FSListingDetail) return;

  const formatNumbers = (scope) => {
    scope.querySelectorAll('.fs-ld-stats dd').forEach((value) => {
      if (value.children.length) return;
      const shown = formatNumber(value.textContent);
      if (shown !== null) {
        value.textContent = shown;
        value.classList.add('fs-ld-num');
        return;
      }
      if (!value.closest('.fs-ld-stat--name')) return;
      const name = properName(value.textContent);
      if (name !== null) value.textContent = name;
    });
  };

  const reduced = () => document.documentElement.dataset.fsMotion === 'reduced' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const narrow = () => window.matchMedia('(max-width: 959px)').matches;

  /* Rareté : même dessin que le snippet fs-listing-tier (crans + mot, jamais la couleur seule). */
  const TIERS = { rarest: ['rare', t('listing.tier.rare'), 3], paid: ['premium', t('listing.tier.premium'), 2] };
  const tierChip = (tier) => {
    const known = TIERS[tier];
    if (!known) return null;
    const [name, word, on] = known;
    const pips = h('span', { class: `fs-tier-pips fs-tier-pips--${name}`, 'aria-hidden': 'true' }, [1, 2, 3].map((i) => h('b', { class: i > on ? 'is-off' : null })));
    return h('span', { class: `fs-tier fs-tier--${name}`, 'data-tier': tier }, pips, word);
  };

  /* ---------------------------------------------------------------- scène et flotte */
  const enhanceStage = (detail) => {
    const tiles = [...detail.querySelectorAll('[data-fs-ld-tile]')];
    if (!tiles.length) return;
    const bay = detail.querySelector('[data-fs-ld-stage] .fs-bay');
    const ship = bay?.querySelector('[data-fs-bay-ship]');
    const images = ship ? [...ship.querySelectorAll('.fs-bay-hero, .fs-bay-refl')] : [];
    const name = bay?.querySelector('[data-fs-bay-name]');
    const tierSlot = bay?.querySelector('[data-fs-bay-tier]');
    const pos = bay?.querySelector('[data-fs-bay-pos]');
    const parks = bay ? [...bay.querySelectorAll('.fs-bay-park-img')] : [];
    const live = detail.querySelector('[data-fs-ld-live]');
    const nav = detail.querySelector('[data-fs-ld-stage-nav]');
    const more = detail.querySelector('[data-fs-ld-fleet-more]');
    const box = detail.querySelector('[data-fs-ld-tiles]');
    const unit = pos?.querySelector('.fs-visually-hidden:last-child')?.textContent || '';
    const moreLabel = more?.textContent.trim() || '';
    let current = Math.max(0, tiles.findIndex((tile) => tile.getAttribute('aria-pressed') === 'true'));
    let expanded = false;
    let timer = 0;

    const limit = () => (narrow() ? 8 : 12);
    const layout = () => {
      const max = limit();
      const folded = !expanded && tiles.length > max;
      tiles.forEach((tile, index) => { tile.hidden = folded && index >= max; });
      box?.setAttribute('data-fs-ld-folded', '');
      if (!more) return;
      more.hidden = tiles.length <= max;
      more.setAttribute('aria-expanded', String(expanded));
      more.textContent = expanded ? t('listing.detail.fleet_less') : moreLabel;
    };
    const copyImage = (from, to) => {
      to.setAttribute('src', from.getAttribute('src'));
      if (from.hasAttribute('srcset')) to.setAttribute('srcset', from.getAttribute('srcset')); else to.removeAttribute('srcset');
      for (const attribute of ['width', 'height', 'data-fs-gp-design', 'data-fs-gp-tier']) {
        if (from.hasAttribute(attribute)) to.setAttribute(attribute, from.getAttribute(attribute));
      }
      for (const name of ['--ship-w', '--ship-w1']) {
        const width = from.style.getPropertyValue(name);
        if (width) to.style.setProperty(name, width); else to.style.removeProperty(name);
      }
    };
    const paint = () => {
      const tile = tiles[current];
      const source = tile.querySelector('img');
      if (source) images.forEach((image) => copyImage(source, image));
      if (name) name.textContent = tile.dataset.fsLdLabel || '';
      if (tierSlot) {
        const chip = tierChip(tile.dataset.tier);
        tierSlot.replaceChildren(...(chip ? [chip] : []));
        tierSlot.hidden = !chip;
      }
      if (pos) pos.replaceChildren(h('span', { class: 'fs-visually-hidden' }, ', '), `${current + 1} / ${tiles.length}`, ...(unit ? [h('span', { class: 'fs-visually-hidden' }, unit)] : []));
      /* Vaisseaux garés : les suivants de la flotte, dans l'ordre (jamais la vedette). */
      parks.forEach((park, index) => {
        const next = tiles[(current + 1 + index) % tiles.length].querySelector('img');
        if (next) copyImage(next, park);
      });
      ship?.classList.remove('is-swapping');
    };
    const select = (index, { focus = false, announce = true } = {}) => {
      current = (index + tiles.length) % tiles.length;
      if (!expanded && current >= limit()) { expanded = true; layout(); }
      tiles.forEach((tile, position) => {
        const on = position === current;
        tile.setAttribute('aria-pressed', String(on));
        tile.tabIndex = on ? 0 : -1;
      });
      if (focus) tiles[current].focus();
      clearTimeout(timer);
      if (!ship || reduced()) paint();
      else { ship.classList.add('is-swapping'); timer = setTimeout(paint, 140); }
      if (announce && live) {
        const tile = tiles[current];
        const word = TIERS[tile.dataset.tier]?.[1];
        live.textContent = t('listing.detail.live', { label: tile.dataset.fsLdLabel, tier: word ? `, ${word}` : '', position: current + 1, total: tiles.length });
      }
    };

    tiles.forEach((tile, index) => {
      tile.tabIndex = index === current ? 0 : -1;
      tile.addEventListener('click', () => {
        select(index);
        /* Téléphone : la scène est au-dessus de la grille ; on y remonte pour voir la vedette. */
        if (narrow()) detail.querySelector('[data-fs-ld-stage]')?.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      });
    });
    box?.addEventListener('keydown', (event) => {
      const tile = event.target.closest?.('[data-fs-ld-tile]');
      if (!tile) return;
      const shown = tiles.filter((item) => !item.hidden);
      const at = shown.indexOf(tile);
      if (at < 0) return;
      const columns = Math.max(1, Math.round(box.getBoundingClientRect().width / Math.max(1, tile.getBoundingClientRect().width)));
      const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns }[event.key];
      let next = null;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = shown.length - 1;
      else if (step !== undefined) next = Math.max(0, Math.min(shown.length - 1, at + step));
      if (next === null) return;
      event.preventDefault();
      shown.forEach((item) => { item.tabIndex = -1; });
      shown[next].tabIndex = 0;
      shown[next].focus();
    });
    /* La tabulation revient toujours sur la vedette en entrant dans le groupe. */
    box?.addEventListener('focusout', (event) => {
      if (box.contains(event.relatedTarget)) return;
      tiles.forEach((tile, index) => { tile.tabIndex = index === current ? 0 : -1; });
    });
    if (nav && tiles.length > 1) {
      nav.hidden = false;
      nav.addEventListener('click', (event) => {
        const button = event.target.closest('[data-fs-ld-step]');
        if (button) select(current + Number(button.dataset.fsLdStep));
      });
    }
    if (more) {
      more.addEventListener('click', () => {
        expanded = !expanded;
        layout();
        if (!expanded && tiles[current].hidden) select(0, { announce: false });
      });
    }
    layout();
    window.matchMedia('(max-width: 959px)').addEventListener?.('change', layout);
  };

  /* ---------------------------------------------------------------- caractéristiques : 8 lignes, puis le reste */
  const enhanceSpecs = (detail) => {
    detail.querySelectorAll('[data-fs-ld-group]').forEach((section) => {
      const list = section.querySelector('[data-fs-ld-rows]');
      const rows = list ? [...list.children] : [];
      if (rows.length <= 8 || section.querySelector('.fs-ld-row-more')) return;
      const extra = rows.slice(8);
      list.id ||= `${section.id}-rows`;
      const label = t('listing.detail.rows_more', { count: extra.length });
      const button = h('button', { type: 'button', class: 'fs-btn fs-btn--link fs-ld-row-more', 'aria-expanded': 'false', 'aria-controls': list.id }, label);
      const set = (open) => {
        extra.forEach((row) => { row.hidden = !open; });
        button.setAttribute('aria-expanded', String(open));
        button.textContent = open ? t('listing.detail.rows_less') : label;
      };
      button.addEventListener('click', () => set(button.getAttribute('aria-expanded') !== 'true'));
      list.after(button);
      set(false);
    });
  };

  /* « Copier » (la référence) et « Partager » (l'adresse de la page de l'annonce, /pages/listings/<handle>) : boutons
     montrés seulement si le presse-papiers est disponible ; un seul état annoncé pour tous. */
  const enhanceCopy = (detail) => {
    const status = detail.querySelector('[data-fs-ld-copy-status]');
    if (!navigator.clipboard?.writeText || !window.isSecureContext) return;
    let timer = 0;
    const say = (text) => {
      if (!status) return;
      status.textContent = text;
      clearTimeout(timer);
      timer = setTimeout(() => { status.textContent = ''; }, 5000);
    };
    const wire = (button, value, done, failed) => {
      if (!button || !value) return;
      button.hidden = false;
      button.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(value());
          say(done);
        } catch {
          say(failed);
        }
      });
    };
    detail.querySelectorAll('[data-fs-ld-copy]').forEach((copy) => {
      const reference = copy.getAttribute('data-fs-ld-copy') || '';
      wire(copy, reference && (() => reference), t('listing.detail.copied', { reference }), t('listing.detail.copy_failed', { reference }));
    });
    detail.querySelectorAll('[data-fs-ld-share]').forEach((share) => {
      const path = share.getAttribute('data-fs-ld-share') || '';
      const pageUrl = () => new URL(path, window.location.origin).href;
      /* Adresse de la langue de la page : « /pages/listings/<handle> » ou « /fr/pages/listings/<handle> ». */
      wire(share, /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/pages\/listings\/[a-z0-9-]+$/i.test(path) && pageUrl, t('listing.detail.link_copied'), t('listing.detail.link_failed'));
    });
  };

  /* « Comparer avec une autre annonce <jeu> » : sélection de fs-compare.js (même clé, même format [{ ref, game }] ; ancien
     format, références seules, encore lu), puis la liste du jeu. Une autre annonce déjà choisie du même jeu (ou de jeu
     inconnu) : &compare=<autre>,<handle> ouvre le face-à-face ; une annonce d'un autre jeu est remplacée. */
  const enhanceCompare = (detail) => {
    const button = detail.querySelector('[data-fs-ld-compare]');
    if (!button) return;
    let store = null;
    try { store = window.sessionStorage; store.getItem('fs-compare'); } catch { return; }
    button.hidden = false;
    button.addEventListener('click', () => {
      const ref = button.dataset.fsLdCompare;
      const game = button.dataset.fsLdCompareGame || '';
      let selection = [];
      try { selection = JSON.parse(store.getItem('fs-compare') || '[]'); } catch { selection = []; }
      const items = (Array.isArray(selection) ? selection : []).map((item) => (typeof item === 'string' ? { ref: item, game: '' } : item))
        .filter((item) => item && typeof item.ref === 'string' && item.ref);
      const found = items.find((item) => item.ref !== ref && (!item.game || item.game === game));
      const other = found ? found.ref : null;
      const kept = other ? [{ ref, game }, { ref: other, game: found.game || game }] : [{ ref, game }];
      try { store.setItem('fs-compare', JSON.stringify(kept)); } catch { /* onglet sans stockage */ }
      const back = detail.querySelector('[data-fs-ld-back]');
      const url = new URL(back?.getAttribute('href') || '/', window.location.href);
      url.search = '';
      url.searchParams.set('game', button.dataset.fsLdCompareGame);
      if (other) url.searchParams.set('compare', `${other},${button.dataset.fsLdCompareHandle}`);
      url.hash = 'fs-catalog';
      window.location.assign(url.href);
    });
  };

  /* Barre d'achat du téléphone : visible quand la zone commerce est hors de l'écran et qu'une action existe. Son bouton
     reprend l'action visible de la fiche (clic transmis), dans l'ordre : acheter, puis ticket. */
  const ACTIONS = [
    ['[data-fs-purchase][data-fs-purchase-ready]', t('listing.detail.buy_short')],
    ['[data-fs-ticket-open]', t('listing.detail.ask')],
    ['[data-fs-ticket-go]', t('listing.detail.continue_ticket')],
  ];
  const enhanceBuybar = (detail) => {
    const bar = detail.querySelector('[data-fs-ld-buybar]');
    const commerce = detail.querySelector('[data-fs-commerce]');
    const cta = bar?.querySelector('[data-fs-ld-buybar-cta]');
    if (!bar || !commerce || !cta || !('IntersectionObserver' in window)) return;
    let away = false;
    const action = () => {
      for (const [selector, label] of ACTIONS) {
        const found = [...detail.querySelectorAll(selector)].find((item) => !item.hidden && !item.closest('[hidden]') && !bar.contains(item) && !item.disabled);
        if (found) return { found, label };
      }
      return null;
    };
    const update = () => {
      const target = action();
      if (target) {
        cta.textContent = target.label;
        cta.setAttribute('aria-label', target.found.textContent.replace(/\s+/g, ' ').trim() || target.label);
      }
      const on = away && Boolean(target);
      bar.hidden = !on;
      detail.toggleAttribute('data-fs-ld-buybar-on', on);
    };
    /* La vraie action est hors de l'écran : la zone d'achat revient d'abord dans la vue (sans animation), pour que la
       suite (erreur du ticket, « Continuer dans mon ticket », fenêtre d'achat) se passe sous les yeux du visiteur. */
    cta.addEventListener('click', () => {
      const target = action();
      if (!target) return;
      (detail.querySelector('.fs-ld-buyzone') || commerce).scrollIntoView({ block: 'start', behavior: 'instant' });
      target.found.click();
    });
    new IntersectionObserver(([entry]) => { away = !entry.isIntersecting; update(); }).observe(commerce);
    /* Actions montées plus tard (fs-purchase.js, fs-ticket.js) : la barre suit. */
    new MutationObserver(update).observe(detail.querySelector('.fs-ld-buyzone') || commerce, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'data-fs-purchase-ready', 'disabled'] });
  };

  /* Proposer pour le même jeu : seulement si la page porte le formulaire (service actif). */
  const enhancePropose = (detail) => {
    const line = detail.querySelector('[data-fs-ld-propose]');
    if (!line) return;
    const root = detail.closest('[data-fs-demo]') || document;
    const available = [...root.querySelectorAll('[data-fs-propose-open]')].some((link) => !detail.contains(link));
    line.hidden = !available;
  };

  const enhanced = new WeakSet();
  const steps = [formatNumbers, enhanceStage, enhanceSpecs, enhanceCopy, enhanceCompare, enhanceBuybar, enhancePropose];
  const enhance = (detail) => {
    if (!detail || enhanced.has(detail)) return;
    enhanced.add(detail);
    for (const step of steps) {
      try { step(detail); } catch (error) { console.warn('[Space Syndicate] fiche : amélioration indisponible', error); }
    }
    detail.setAttribute('data-fs-ld-ready', '');
  };
  const scan = (node) => {
    if (node.nodeType !== 1) return;
    if (node.matches('[data-fs-ld]')) enhance(node);
    node.querySelectorAll('[data-fs-ld]').forEach(enhance);
  };

  const observer = new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach(scan)));
  const watched = new WeakSet();
  const watch = (scope) => scope.querySelectorAll('[data-fs-detail]').forEach((slot) => {
    if (watched.has(slot)) return;
    watched.add(slot);
    observer.observe(slot, { childList: true });
    scan(slot);
  });
  watch(document);
  document.addEventListener('shopify:section:load', (event) => watch(event.target));
  window.FSListingDetail = Object.freeze({ enhance, formatNumber, properName });
})();
